import { useMutation, type UseMutationResult } from '@tanstack/react-query';

import { apiRequest, type ApiError } from '@/lib/api-client';
import { useSession } from '@/lib/session';

/** Matches apps/api's `TokenResponse` exactly (auth.controller.ts). */
export interface TokenResponse {
  readonly accessToken: string;
  readonly refreshToken: string;
  readonly refreshExpiresAt: string;
  /** The password that opened this session is a provisional one: the only thing the account may do is choose its own. */
  readonly mustChangePassword: boolean;
}

export interface ChangePasswordInput {
  readonly currentPassword: string;
  readonly newPassword: string;
}

export interface LoginInput {
  readonly email: string;
  readonly password: string;
}

export function useLoginMutation(): UseMutationResult<TokenResponse, ApiError, LoginInput> {
  return useMutation({
    mutationFn: (input: LoginInput) =>
      apiRequest<TokenResponse>('/auth/login', { method: 'POST', body: input }),
  });
}

/**
 * `POST /auth/password/change` - the way out of a provisional password, and an ordinary change. Returns a FRESH session (every earlier
 * one has ended). The variables carry both passwords: the mutation is collected at once (`gcTime: 0`) and the page resets it.
 */
export function useChangePasswordMutation(): UseMutationResult<
  TokenResponse,
  ApiError,
  ChangePasswordInput
> {
  const { accessToken } = useSession();
  return useMutation({
    gcTime: 0,
    mutationFn: (input: ChangePasswordInput) =>
      apiRequest<TokenResponse>('/auth/password/change', {
        method: 'POST',
        body: input,
        accessToken,
      }),
  });
}
